import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, Monitor, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import keishIcon from "@/assets/icon-green.svg";
import keishIconWhite from "@/assets/icon-white.svg";
import ContactSheet from "@/components/ContactSheet";
import BookingStatus from "@/components/BookingStatus";
import { useTheme } from "@/components/ThemeProvider";

const navItems = [
  { id: "hello", label: "Hello!", number: "01" },
  { id: "portfolio", label: "Portfolio", number: "02" },
  { id: "about", label: "About", number: "03" },
  { id: "services", label: "Services", number: "04" },
  { id: "shop", label: "Shop", number: "05" },
  { id: "testimonials", label: "Testimonials", number: "06" },
];

interface SidebarProps {
  activeSection: string;
  onNavigate: (id: string) => void;
}

const ThemeToggle = ({ isDark }: { isDark: boolean }) => {
  const { mode, setMode } = useTheme();

  return (
    <div className="flex items-center gap-1 mb-5">
      <button
        onClick={() => setMode("light")}
        className="p-1.5 rounded-full transition-all duration-300"
        style={{
          opacity: mode === "light" ? 1 : 0.47,
          backgroundColor: mode === "light"
            ? isDark ? "hsla(27, 61%, 92%, 0.12)" : "hsla(0, 0%, 9%, 0.08)"
            : "transparent",
        }}
        aria-label="Light mode"
      >
        <Sun size={13} strokeWidth={1.5} />
      </button>
      <button
        onClick={() => setMode("dark")}
        className="p-1.5 rounded-full transition-all duration-300"
        style={{
          opacity: mode === "dark" ? 1 : 0.47,
          backgroundColor: mode === "dark"
            ? isDark ? "hsla(27, 61%, 92%, 0.12)" : "hsla(0, 0%, 9%, 0.08)"
            : "transparent",
        }}
        aria-label="Dark mode"
      >
        <Moon size={13} strokeWidth={1.5} />
      </button>
    </div>
  );
};

const Sidebar = ({ activeSection, onNavigate }: SidebarProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [time, setTime] = useState(new Date());
  const { mode } = useTheme();
  const location = useLocation();

  const isSubPage = location.pathname.startsWith("/clients") || location.pathname.startsWith("/work/") || location.pathname === "/work";
  const isClientsPage = location.pathname === "/clients";
  const isCaseStudyPage = location.pathname.startsWith("/work/") || location.pathname === "/work";

  const isDark = mode === "dark";

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const handleNav = (id: string) => {
    onNavigate(id);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3 backdrop-blur-xl lg:hidden transition-colors duration-500"
        style={{
          backgroundColor: isDark ? "hsla(0, 0%, 9%, 0.9)" : "hsla(45, 100%, 98%, 0.9)",
          color: isDark ? "hsl(27, 61%, 92%)" : "hsl(0, 0%, 9%)",
        }}
      >
        <Link to="/" className="flex items-center gap-2" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <img src={isDark ? keishIconWhite : keishIcon} alt="KEISH" className="w-6 h-6 transition-opacity duration-500" />
          <span className="font-body text-ts-2xs font-semibold tracking-[0.2em] uppercase">
            KEISH
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <MobileThemeToggle isDark={isDark} />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-1.5"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      {/* Mobile menu — full-screen editorial takeover */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden flex flex-col"
            style={{
              backgroundColor: isDark ? "hsl(0, 0%, 9%)" : "hsl(45, 100%, 98%)",
              color: isDark ? "hsl(27, 61%, 92%)" : "hsl(0, 0%, 9%)",
            }}
          >
            {/* Spacer for header */}
            <div className="h-[49px] flex-shrink-0" />

            {/* Main nav links — large editorial type */}
            <nav className="flex-1 flex flex-col justify-center px-8 -mt-8">
              {navItems.map((item, i) => {
                const isPortfolio = item.id === "portfolio";
                const isActive = isPortfolio
                  ? activeSection === "portfolio" || isSubPage
                  : activeSection === item.id && !isSubPage;

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: i * 0.06, duration: 0.35, ease: "easeOut" }}
                  >
                    <button
                      onClick={() => handleNav(item.id)}
                      className="group w-full text-left py-2 flex items-baseline gap-4 transition-opacity duration-300"
                      style={{ opacity: isActive ? 1 : 0.35 }}
                    >
                      <span className="font-body text-ts-2xs opacity-50 w-5 translate-y-[2px]">
                        {item.number}
                      </span>
                      <span className="font-display text-[clamp(2rem,8vw,3.5rem)] italic leading-[1.1] tracking-tight">
                        {item.label}
                      </span>
                    </button>

                    {/* Portfolio sub-links */}
                    {isPortfolio && (isActive || isSubPage) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="ml-9 flex gap-4 pb-1"
                      >
                        <Link
                          to="/work"
                          onClick={() => setMobileOpen(false)}
                          className={`font-body text-ts-2xs tracking-[0.15em] uppercase transition-opacity duration-300 ${
                            isCaseStudyPage || isClientsPage ? "opacity-90 border-b border-current pb-0.5" : "opacity-40"
                          }`}
                        >
                          Case Studies & Clients
                        </Link>
                      </motion.div>
                    )}

                    {/* About sub-link: Playbook */}
                    {item.id === "about" && isActive && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="ml-9 pb-1"
                      >
                       <button
                         onClick={() => {
                           const el = document.querySelector('[data-playbook]');
                           if (el) el.scrollIntoView({ behavior: 'smooth' });
                           setMobileOpen(false);
                         }}
                         className="font-body text-ts-2xs tracking-[0.15em] uppercase transition-opacity duration-300 opacity-40 hover:opacity-90"
                       >
                         Our Playbook
                       </button>
                      </motion.div>
                    )}

                    {/* Services sub-link: Dallas Locals */}
                    {item.id === "services" && isActive && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="ml-9 pb-1"
                      >
                       <button
                          onClick={() => {
                            const el = document.getElementById('dallas-locals');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                            setMobileOpen(false);
                          }}
                          className="font-body text-ts-2xs tracking-[0.15em] uppercase transition-opacity duration-300 opacity-40 hover:opacity-90"
                        >
                           Locals Only Club
                        </button>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </nav>

            {/* Bottom section */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="px-8 pb-8"
            >
              <button
                  onClick={() => {
                    setMobileOpen(false);
                    setTimeout(() => setContactOpen(true), 400);
                  }}
                  className="w-full text-center py-3 font-body text-ts-2xs font-medium tracking-[0.2em] uppercase border transition-colors duration-500"
                  style={{ borderColor: "currentColor" }}
                >
                  Work With Us
                </button>
              <div className="flex items-center justify-between mt-4">
                <span className="font-body text-ts-2xs tracking-wide opacity-50">
                  Dallas, TX
                </span>
                <span className="font-body text-ts-2xs tabular-nums opacity-40">
                  {time.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex fixed top-0 left-0 bottom-0 w-[224px] flex-col justify-between z-50 transition-colors duration-500"
        style={{
          backgroundColor: isDark ? "hsl(0, 0%, 9%)" : "hsl(45, 100%, 98%)",
          color: isDark ? "hsl(27, 61%, 92%)" : "hsl(0, 0%, 9%)",
          borderRightWidth: 1,
          borderColor: isDark ? "hsla(27, 61%, 92%, 0.1)" : "hsl(45, 10%, 88%)",
        }}
      >
        {/* Top: Branding */}
        <div>
          <div className="px-5 pt-6 pb-5">
            <div className="flex items-center gap-2.5">
              <img src={isDark ? keishIconWhite : keishIcon} alt="KEISH" className="w-6 h-6 transition-opacity duration-500" />
            </div>

            <div className="mt-5">
              <p className="font-heading text-ts-2xs font-bold tracking-[0.12em] uppercase leading-tight">
                KEISH Design Studio
              </p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="font-body text-ts-2xs tracking-wide opacity-75">
                  Dallas, TX
                </span>
                <span className="opacity-50">·</span>
                <span className="font-body text-ts-2xs tabular-nums opacity-70">
                  {time.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: true,
                  })}
                </span>
              </div>
            </div>
          </div>


          {/* Navigation */}
          <nav className="flex flex-col py-4">
            {navItems.map((item) => {
              const isPortfolio = item.id === "portfolio";
              const isActive = isPortfolio
                ? activeSection === "portfolio" || isSubPage
                : activeSection === item.id && !isSubPage;

              return (
                <div key={item.id}>
                  <button
                    onClick={() => handleNav(item.id)}
                    className={`w-full flex items-center gap-3 px-5 py-[9px] text-left transition-all duration-300 font-body text-ts-xs tracking-[0.02em] ${
                      isActive
                        ? isDark
                          ? "bg-[hsl(27,61%,92%)] text-[hsl(0,0%,9%)] font-medium"
                          : "bg-primary text-primary-foreground font-medium"
                        : "opacity-70 hover:opacity-100"
                    }`}
                  >
                    <span className="text-ts-2xs font-body opacity-90 w-4">{item.number}</span>
                    {item.label}
                  </button>

                   {/* Portfolio sub-links — blockquote treatment */}
                   {isPortfolio && (isActive || isSubPage) && (
                    <div className="flex flex-col ml-12 mr-5 border-l py-1 my-0.5 transition-colors duration-500"
                      style={{
                        borderColor: isDark ? "hsla(27, 61%, 92%, 0.15)" : "hsla(0, 0%, 9%, 0.12)",
                      }}
                    >
                      <Link
                        to="/work"
                        className={`group/sublink pl-3 py-1 font-body text-ts-2xs tracking-[0.03em] transition-all duration-300 flex items-center gap-1 ${
                          isCaseStudyPage || isClientsPage
                            ? "opacity-100 font-medium"
                            : "opacity-50 hover:opacity-85"
                        }`}
                      >
                        Case Studies & Clients
                        <ArrowUpRight size={10} className="opacity-0 group-hover/sublink:opacity-70 transition-opacity duration-200 -translate-y-px" />
                      </Link>
                    </div>
                  )}

                   {/* About sub-link: Playbook */}
                   {item.id === "about" && isActive && (
                    <div className="flex flex-col ml-12 mr-5 border-l py-1 my-0.5 transition-colors duration-500"
                      style={{
                        borderColor: isDark ? "hsla(27, 61%, 92%, 0.15)" : "hsla(0, 0%, 9%, 0.12)",
                      }}
                    >
                       <button
                         onClick={() => {
                           const el = document.querySelector('[data-playbook]');
                           if (el) el.scrollIntoView({ behavior: 'smooth' });
                         }}
                         className="pl-3 py-1 font-body text-ts-2xs tracking-[0.03em] transition-all duration-300 opacity-50 hover:opacity-85 text-left"
                       >
                         Our Playbook
                       </button>
                    </div>
                  )}

                   {/* Services sub-link: Dallas Locals */}
                   {item.id === "services" && isActive && (
                    <div className="flex flex-col ml-12 mr-5 border-l py-1 my-0.5 transition-colors duration-500"
                      style={{
                        borderColor: isDark ? "hsla(27, 61%, 92%, 0.15)" : "hsla(0, 0%, 9%, 0.12)",
                      }}
                    >
                       <button
                          onClick={() => {
                            const el = document.getElementById('dallas-locals');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="pl-3 py-1 font-body text-ts-2xs tracking-[0.03em] transition-all duration-300 opacity-50 hover:opacity-85 text-left"
                        >
                           Locals Only Club
                        </button>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Bottom: Actions */}
        <div className="px-5 pb-5">
          <ThemeToggle isDark={isDark} />
          <ContactSheet>
            <button
              className="w-full text-center py-2.5 font-body text-ts-2xs font-medium tracking-[0.2em] uppercase border transition-colors duration-500 hover:opacity-80"
              style={{
                borderColor: "currentColor",
              }}
            >
              Work With Us
            </button>
          </ContactSheet>
        </div>
      </aside>

      {/* Externally controlled contact sheet for mobile nav */}
      <ContactSheet
        externalOpen={contactOpen}
        onExternalOpenChange={setContactOpen}
      >
        <button className="hidden" aria-hidden="true" tabIndex={-1} />
      </ContactSheet>
    </>
  );
};

/** Compact mobile theme toggle */
const MobileThemeToggle = ({ isDark }: { isDark: boolean }) => {
  const { mode, setMode } = useTheme();

  return (
    <button
      onClick={() => setMode(mode === "light" ? "dark" : "light")}
      className="p-1.5 transition-colors"
      aria-label={`Switch to ${mode === "light" ? "dark" : "light"} mode`}
      title={`Switch to ${mode === "light" ? "dark" : "light"} mode`}
    >
      {mode === "light" ? <Moon size={14} /> : <Sun size={14} />}
    </button>
  );
};

export default Sidebar;
